import React, { useMemo, useEffect, useState } from 'react';

export default function SupportTicketFormOverlay({ email, defaultFilename, onClose, onSubmitted }) {
  const [type, setType] = useState('problem');   // "problem" | "request"
  const [title, setTitle] = useState('');
  const [project, setProject] = useState(defaultFilename || '');
  const [projectOptions, setProjectOptions] = useState([]);
  const [contents, setContents] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // Load subdirectories for this user's folder
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`/user-folders/?email=${encodeURIComponent(email || '')}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!mounted) return;

        const opts = Array.isArray(data.folders) ? data.folders : [];
        setProjectOptions(opts);

        // Initialize selection
        if (opts.length > 0) {
          setProject((prev) => (prev && opts.includes(prev) ? prev : opts[0]));
        } else {
          setProject('');
        }
      } catch (e) {
        console.error('Failed to load user folders:', e);
        if (mounted) {
          setProjectOptions([]);
          setProject('');
        }
      }
    })();
    return () => { mounted = false; };
  }, [email]);

  // Format like "YYYY-MM-DD HH:MM:SS"
  const nowStamp = useMemo(() => {
    const pad = (n) => String(n).padStart(2, '0');
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }, []);

  // Trim label after the first "-"
  const projectLabel = (name) => {
    if (!name) return '';
    const i = name.indexOf('-');
    return i >= 0 ? name.slice(i + 1) : name;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);

    if (!title.trim())     { setErr('Please enter a title.'); return; }
    if (!contents.trim())  { setErr('Please enter a description.'); return; }

    try {
      setSubmitting(true);

      const payload = {
        timestamp: nowStamp,
        username: email || 'unknown',
        filename: project || '',      // store ORIGINAL folder name for reliability
        type,                         // "problem" or "request"
        title: title.trim(),
        contents: contents.trim(),
        status: 'open',
        reply: '',
      };

      const res = await fetch('/support-tickets/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(`Submit failed (${res.status}) ${msg}`);
      }

      onSubmitted?.(payload);
      onClose?.();
    } catch (e2) {
      console.error(e2);
      setErr(e2.message || 'Failed to submit ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={backdropStyle}>
      <div style={modalStyle}>
        <div style={headerRowStyle}>
          <h3 style={{ margin: 0 }}>Submit a Support Ticket</h3>
          <button onClick={onClose} style={buttonStyle('#6b7280')}>Close</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={rowStyle}>
            <label style={labelStyle}>Email</label>
            <input value={email || ''} disabled style={inputStyle} />
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
              <option value="problem">Problem</option>
              <option value="request">Request</option>
            </select>
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Project</label>
            <select
              value={project}
              onChange={(e) => setProject(e.target.value)}
              style={inputStyle}
              disabled={projectOptions.length === 0}
            >
              {projectOptions.length === 0 ? (
                <option value="">No projects found</option>
              ) : (
                projectOptions.map((name) => (
                  <option key={name} value={name}>
                    {projectLabel(name)}
                  </option>
                ))
              )}
            </select>
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short summary"
              style={inputStyle}
            />
          </div>

          <div style={rowStyleColumn}>
            <label style={labelStyle}>Description</label>
            <textarea
              value={contents}
              onChange={(e) => setContents(e.target.value)}
              placeholder="Describe the problem or request…"
              rows={6}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {err && <div style={{ color: '#b91c1c', fontSize: 13 }}>{err}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} style={buttonStyle('#9ca3af')}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={buttonStyle('#16a34a')}>
              {submitting ? 'Sending…' : 'Send Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Styles */
const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10001,
};

const modalStyle = {
  background: '#fff',
  padding: 20,
  borderRadius: 12,
  width: '90%',
  maxWidth: 640,
  maxHeight: '85vh',
  overflow: 'auto',
  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const headerRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const rowStyle = {
  display: 'grid',
  gridTemplateColumns: '180px 1fr',
  alignItems: 'center',
  gap: 10,
};

const rowStyleColumn = {
  display: 'grid',
  gridTemplateColumns: '180px 1fr',
  alignItems: 'start',
  gap: 10,
};

const labelStyle = { fontWeight: 600, color: '#374151', fontSize: 14 };

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

function buttonStyle(bg) {
  return {
    padding: '8px 14px',
    backgroundColor: bg,
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  };
}
